import type { ReactNode } from "react";
import { formatNodes, formatTemplate } from "./format.js";
import { DEFAULT_LOCALE, getLanguage, getMessages, languageFor } from "./lang-router.js";
import type { MessageKey, PluralKey, Vars } from "./types.js";

// Translation bound to the language router's current language. Route loaders and
// actions import these directly; components use the hooks in `i18n.tsx`, which
// also re-render when the language changes.

type Catalog = Readonly<Record<string, string | undefined>>;

function lookup(key: string): string | undefined {
  const active = getMessages() as Catalog;
  const fallback = languageFor(DEFAULT_LOCALE).messages as Catalog;
  return active[key] ?? fallback[key];
}

/** Translates a known key. A key missing from every catalog renders as itself, never as blank. */
export function translate(key: MessageKey, vars?: Vars): string {
  return formatTemplate(lookup(key) ?? key, vars);
}

/**
 * Translates a key built from data (a status, a provenance, a finding class).
 * Contract enums grow faster than catalogs, so an unknown value keeps showing
 * the raw value from the engine instead of a broken-looking key.
 */
export function translateOr(key: string, fallback: string, vars?: Vars): string {
  return formatTemplate(lookup(key) ?? fallback, vars);
}

/** Translates a message whose placeholders are React nodes (links, `<code>`, badges). */
export function translateNodes(key: MessageKey, parts: Readonly<Record<string, ReactNode>>): ReactNode {
  return formatNodes(lookup(key) ?? key, parts);
}

/** Picks `<base>.one` / `<base>.other` through `Intl.PluralRules`; `{count}` is always available. */
export function translatePlural(base: PluralKey, count: number, vars?: Vars): string {
  const category = new Intl.PluralRules(getLanguage().tag).select(count);
  const message = lookup(`${base}.${category}`) ?? lookup(`${base}.other`) ?? base;
  return formatTemplate(message, { count, ...vars });
}

export function formatDate(value: string | number | Date): string {
  return new Date(value).toLocaleDateString(getLanguage().tag);
}

export function formatDateTime(value: string | number | Date): string {
  return new Date(value).toLocaleString(getLanguage().tag);
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(getLanguage().tag, options).format(value);
}
